import Box from '@mui/material/Box';
import CameraIcon from '@mui/icons-material/PhotoCameraOutlined';
import CompareIcon from '@apps-common/ui-icons/Compare';
import PhotoFilterIcon from '@mui/icons-material/PhotoFilter';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect} from 'react';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';

import Container from '../side-container.jsx';
import ProcessChooserIcon from '@explorer/components/sidebars/process-chooser/icon';
import ToggleButton from '../toggle-button.jsx';
import {
  DRAWERS,
  ENHANCEMENT_MODE,
  LABS_MAP,
  MODALS,
  PROCESS_MAP,
  TILE_MODE,
  TOUR_STAGES,
  UI_MODES,
} from '@explorer/constants';
import {
  canEnhance,
  canProcess,
  isAnalytic,
} from '@explorer/selectors/capabilities';
import {getSRSeries} from '@explorer/selectors/series';
import {getSelectedResultItemType} from '@explorer/selectors/folders';
import {setColorEnhanceMode, setProcess} from '@explorer/actions/tiles';
import {
  setMode,
  showLeftDrawer,
  showModal,
  showRightDrawer,
} from '@explorer/actions/ui';
import {trackEvent} from '@explorer/analytics';
import {withDarkTheme} from '@explorer/styles';

const getProcessTooltipKey = (isDaily, isAnalytic, srSeries, isFolders) => {
  let message = 'navbars.tooltipProcessDefault';

  if (!isAnalytic && isFolders) {
    message = 'navbars.tooltipProcessUnavailableForItem';
  } else if (
    (isDaily && !isFolders) ||
    (!isAnalytic &&
      ((!isDaily && !isFolders && srSeries.length === 0) || isFolders))
  ) {
    message = 'navbars.tooltipProcessUnavailableForMap';
  } else if (!isAnalytic && srSeries.length > 0) {
    message = 'navbars.tooltipProcessRequiresSpectralAnalysis';
  }

  return message;
};

const RightNav = ({
  showRightDrawer,
  setColorEnhanceMode,
  setProcess,
  ...props
}) => {
  const {t} = useTranslation();
  const isDaily = props.tileMode === TILE_MODE.RESULTS;
  const isFolders = props.tileMode === TILE_MODE.FOLDERS;

  const processTooltip = getProcessTooltipKey(
    isDaily,
    props.isAnalytic,
    props.srSeries,
    isFolders
  );
  const processActive = props.process && props.process !== 'rgb';

  const colorEnhanceDisabled =
    props.mode === UI_MODES.COMPARE || !props.canEnhance;

  const resetEnhanceMode = useCallback(() => {
    setColorEnhanceMode({mode: ENHANCEMENT_MODE.OFF});
    showRightDrawer('');
  }, [setColorEnhanceMode, showRightDrawer]);

  const resetProcessMode = useCallback(() => {
    setProcess(PROCESS_MAP.RGB);
    showRightDrawer('');
  }, [setProcess, showRightDrawer]);

  /** Snapshot and Compare are mutually exclusive (for now) */
  const handleMode = (event, value) => {
    if (value === 'stories') {
      props.showModal(MODALS.STORIES);
      return;
    }
    const isActive = props.mode === value;
    const newMode = isActive ? UI_MODES.BROWSE : value;

    props.setMode(newMode);

    if (newMode === UI_MODES.COMPARE) {
      trackEvent('Turned on Compare Mode');
      if (newMode === UI_MODES.SNAPSHOT && props.leftSidebarVisible) {
        props.showLeftDrawer('');
      }
      // close auto enhance
      if (props.rightDrawer === DRAWERS.COLORENHANCE) {
        resetEnhanceMode();
      }
    }
  };

  const handleRightDrawers = (event, value) => {
    const newValue = props.rightDrawer === value ? '' : value;
    if (newValue === DRAWERS.PROCESS) {
      trackEvent('Clicked process menu button', {
        analytic: props.isAnalytic,
        mode: props.tileMode,
        isSkySat: isFolders && props.folderItemIsSkySat,
      });

      if (!props.isAnalytic) {
        props.showModal(MODALS.SR_PROMPT);
        return;
      }
    } else if (newValue === DRAWERS.COLORENHANCE) {
      trackEvent('Clicked color enhance button', {
        mode: props.tileMode,
        isSkySat: isFolders && props.folderItemIsSkySat,
      });
    }
    showRightDrawer(newValue);
  };

  // Close drawers
  useEffect(() => {
    if (isDaily && !isFolders) {
      showRightDrawer('');
    }
  }, [isDaily, isFolders, showRightDrawer]);

  useEffect(() => {
    if (colorEnhanceDisabled) {
      resetEnhanceMode();
    }
  }, [colorEnhanceDisabled, resetEnhanceMode]);

  useEffect(() => {
    if (!props.canProcess && props.process === null) {
      resetProcessMode();
    }
  }, [props.canProcess, props.process, resetProcessMode]);

  return (
    <Container>
      <Box flexGrow={1}>
        <ToggleButton
          value={UI_MODES.COMPARE}
          disabled={isFolders || props.mode === UI_MODES.SNAPSHOT}
          selected={props.mode === UI_MODES.COMPARE}
          onClick={handleMode}
          title={t('navbars.tooltipCompare')}
          data-tour={TOUR_STAGES.COMPARE}
        >
          <CompareIcon />
        </ToggleButton>

        <ToggleButton
          value={DRAWERS.COLORENHANCE}
          onClick={handleRightDrawers}
          selected={
            props.rightDrawer === DRAWERS.COLORENHANCE || props.enhancementOn
          }
          title={
            colorEnhanceDisabled
              ? t('navbars.tooltipEnhanceUnavailable')
              : t('navbars.tooltipEnhance')
          }
          disabled={colorEnhanceDisabled}
          data-qe="auto-enhance-tools"
        >
          <PhotoFilterIcon />
        </ToggleButton>

        <ToggleButton
          value={DRAWERS.PROCESS}
          selected={props.rightDrawer === DRAWERS.PROCESS || processActive}
          onClick={handleRightDrawers}
          title={t(processTooltip)}
          disabled={!props.canProcess}
          data-qe="process-chooser"
        >
          <ProcessChooserIcon />
        </ToggleButton>

        {props.snapshotLab && (
          <ToggleButton
            value="snapshot"
            onClick={handleMode}
            selected={props.mode === UI_MODES.SNAPSHOT}
            disabled={props.mode === UI_MODES.COMPARE}
            title={t('navbars.tooltipScreenshot')}
          >
            <CameraIcon />
          </ToggleButton>
        )}
        <ToggleButton
          value="stories"
          onClick={handleMode}
          disabled={isFolders}
          title={
            isFolders
              ? t('navbars.tooltipStoriesUnavailable')
              : t('navbars.tooltipStories')
          }
        >
          <TimelapseIcon />
        </ToggleButton>
      </Box>
    </Container>
  );
};

RightNav.propTypes = {
  mode: PropTypes.string.isRequired,
  showModal: PropTypes.func,
  setMode: PropTypes.func,
  leftSidebarVisible: PropTypes.bool,
  showLeftDrawer: PropTypes.func,
  showRightDrawer: PropTypes.func,
  snapshotLab: PropTypes.bool,
  isAnalytic: PropTypes.bool,
  rightDrawer: PropTypes.string,
  process: PropTypes.string,
  enhancementOn: PropTypes.bool,
  srSeries: PropTypes.array,
  tileMode: PropTypes.string,
  setColorEnhanceMode: PropTypes.func,
  folderItemIsSkySat: PropTypes.bool,
  canEnhance: PropTypes.bool,
  canProcess: PropTypes.bool,
  setProcess: PropTypes.func,
};

const mapStateToProps = state => ({
  mode: state.ui.mode,
  leftSidebarVisible: state.ui.drawers.left !== '',
  snapshotLab: state.labs[LABS_MAP.SNAPSHOT],
  isAnalytic: isAnalytic(state),
  canEnhance: canEnhance(state),
  canProcess: canProcess(state),
  rightDrawer: state.ui.drawers.right,
  process: state.tiles.modeSettings[state.tiles.mode].process,
  enhancementOn:
    state.tiles.modeSettings[state.tiles.mode].enhanceMode !==
    ENHANCEMENT_MODE.OFF,
  srSeries: getSRSeries(state),
  tileMode: state.tiles.mode,
  folderItemIsSkySat: getSelectedResultItemType(state).includes('SkySat'),
});

const mapDispatchProps = {
  showModal,
  setMode,
  showLeftDrawer,
  showRightDrawer,
  setColorEnhanceMode,
  setProcess,
};

export default connect(
  mapStateToProps,
  mapDispatchProps
)(withDarkTheme(RightNav));
